import { useEffect, useState } from 'react';

import { getFormattedPrice } from '@/components/wpl/pricing/src/price-finder/utils/amounts-format';
import type { CCPOfferingContent, CCPPricingPlanContent } from '@/contentful/product-catalog';
import { useCCPPriceFinderData } from '@/contentful/product-catalog';
import { offeringSkus as twcOfferingSkus } from '@/templates/collections/teamwork/pricing/constants';
import {
	type CurrencyISOCode,
	getCurrencyFromUserLocale,
	useGeoLocation,
} from '@/utils/useGeoCurrency';

import { loomOfferingSkus } from '../../../uniform/components/pricing-calculator/src/configs/loom.pricing-calculator-config.ts';

import { DEFAULT_TWC_PRICE } from './messages';

/**
 * STARB-4569 — live, localized price for the Loom Land opt-in choice cards.
 *
 * The compared TWC edition depends on the Loom edition: Business + AI vs TWC
 * Premium; Business / Starter (free) vs TWC Standard. Business shows a per-user
 * monthly delta ("more"/"less"); Starter shows the raw TWC price ("absolute").
 * Prices come from the CCP payload (geo-picked currency); any miss falls back to
 * {@link DEFAULT_TWC_PRICE}.
 */

/**
 * Loom edition from the `edition` query param; "premium" = Business + AI.
 *
 * Source of truth: the value comes from the signup surface, which appends
 * `&edition=${products[0].edition}` to the continue URL. `free`/`standard`/
 * `premium` match the canonical signup `Edition`
 * (`@/components/signup/utils/products`); `starter`/`business`/`enterprise` are
 * additional Loom-only aliases accepted here that reuse existing SKUs (see
 * LOOM_EDITION_TO_SKU: `starter` → free, `standard`/`business` → business).
 * There is no single shared type covering all six, so this stays a literal union.
 * `resolveLoomEdition` normalizes anything unknown/missing to `premium`.
 */
export type LoomEdition = 'free' | 'starter' | 'business' | 'standard' | 'premium' | 'enterprise';

// Loom edition → CCP SKU. `standard` reuses `business` and `starter` reuses
// `free` (Loom has no separate standard/starter SKU).
const LOOM_EDITION_TO_SKU: Record<LoomEdition, string> = {
	free: loomOfferingSkus.free,
	starter: loomOfferingSkus.free,
	business: loomOfferingSkus.business,
	standard: loomOfferingSkus.business,
	premium: loomOfferingSkus.businessPlusAi,
	enterprise: loomOfferingSkus.enterprise,
};

// Loom edition → the TWC edition we compare against (Business + AI vs Premium,
// everything else vs Standard).
const LOOM_EDITION_TO_TWC_SKU: Record<LoomEdition, string> = {
	free: twcOfferingSkus.standard,
	starter: twcOfferingSkus.standard,
	business: twcOfferingSkus.standard,
	standard: twcOfferingSkus.standard,
	premium: twcOfferingSkus.premium,
	enterprise: twcOfferingSkus.premium,
};

/** Which price message to render: delta "more"/"less", or the raw "absolute" price. */
export type TwcPriceMode = 'more' | 'less' | 'absolute';

// Editions where we show the raw TWC price instead of a delta.
const ABSOLUTE_PRICE_EDITIONS = new Set<LoomEdition>(['free', 'starter']);

const DEFAULT_LOOM_EDITION: LoomEdition = 'premium';

/**
 * Resolves a Loom edition string (case-insensitive) to a known {@link LoomEdition},
 * defaulting to Business + AI (premium) for anything unrecognized or missing.
 */
export const resolveLoomEdition = (edition: string | null | undefined): LoomEdition => {
	const normalized = edition?.toLowerCase();
	if (normalized && normalized in LOOM_EDITION_TO_SKU) {
		return normalized as LoomEdition;
	}
	return DEFAULT_LOOM_EDITION;
};

// CCP amounts come back in minor units. We only support USD and JPY: USD is in
// cents (divide by 100), JPY has no minor units and is already whole (use as-is).
// Mirrors the 'USD' | 'JPY' handling in ccp-to-hams-pricing-plan-converter and
// the JPY special-case in amounts-format.
const toMajorUnits = (minorAmount: number, currency: CurrencyISOCode): number =>
	currency === 'JPY' ? minorAmount : minorAmount / 100;

/**
 * Reads the per-user, monthly, first-tier amount (in major units) for the given
 * currency from a CCP offering. Returns `undefined` when the offering has no
 * matching monthly per-user plan (e.g. a free offering with no plan, or an
 * enterprise offering that is annual/quote-based only).
 */
const readMonthlyPerUserAmount = (
	offering: CCPOfferingContent | undefined,
	currency: CurrencyISOCode,
): number | undefined => {
	const plan = offering?.defaultPricingPlans?.find(
		(p: CCPPricingPlanContent) => p.currency === currency && p.primaryCycle_name === 'MONTHLY',
	);
	const userItem = plan?.items.find((item) => item.chargeElement === 'user');
	const firstTier = userItem?.tiers[0];
	if (!firstTier || typeof firstTier.unitAmount !== 'number') {
		return undefined;
	}
	return toMajorUnits(firstTier.unitAmount, currency);
};

/**
 * Loom baseline (major units) to subtract from the TWC price: the monthly
 * per-user amount, `0` for FREE offerings, or `undefined` when there's no
 * monthly rate (enterprise / annual-only) so the caller falls back.
 */
const resolveLoomBaseline = (
	offering: CCPOfferingContent | undefined,
	currency: CurrencyISOCode,
): number | undefined => {
	const amount = readMonthlyPerUserAmount(offering, currency);
	if (typeof amount === 'number') {
		return amount;
	}
	if (offering?.pricingType === 'FREE') {
		return 0;
	}
	return undefined;
};

export interface UseTwcPriceResult {
	/** Formatted per-user / month amount (delta for more/less, raw for absolute). */
	price: string;
	/** Which choice-card message to render for {@link price}. */
	mode: TwcPriceMode;
	/** True while geo/CCP data is still being fetched. */
	isLoading: boolean;
	/**
	 * True when the CCP price fetch rejected or did not complete within
	 * {@link PRICE_FETCH_TIMEOUT_MS}. Callers should stop waiting on the price and
	 * fall back (e.g. redirect to the Loom-only signup) rather than spin forever.
	 */
	hasError: boolean;
}

/** How long to wait for geo + CCP before treating the price fetch as failed. */
const PRICE_FETCH_TIMEOUT_MS = 10_000;

// Fallback reads as a "more" delta.
const DEFAULT_RESULT_MODE: TwcPriceMode = 'more';

const DEFAULT_RESULT: UseTwcPriceResult = {
	price: DEFAULT_TWC_PRICE,
	mode: DEFAULT_RESULT_MODE,
	isLoading: false,
	hasError: false,
};

/** Terminal result when the price fetch fails or times out. */
const ERROR_RESULT: UseTwcPriceResult = {
	price: DEFAULT_TWC_PRICE,
	mode: DEFAULT_RESULT_MODE,
	isLoading: false,
	hasError: true,
};

/** Formats an amount, falling back to the default result on any format error. */
const formatResult = (
	currency: CurrencyISOCode,
	amount: number,
	mode: TwcPriceMode,
): UseTwcPriceResult => {
	try {
		return { price: getFormattedPrice(currency, amount), mode, isLoading: false, hasError: false };
	} catch {
		return DEFAULT_RESULT;
	}
};

/** Derives the display result from already-fetched CCP amounts. */
const resolvePriceResult = (
	twcAmount: number | undefined,
	loomBaseline: number | undefined,
	currency: CurrencyISOCode,
	isAbsolute: boolean,
): UseTwcPriceResult => {
	// Missing TWC amount → fall back.
	if (typeof twcAmount !== 'number' || !Number.isFinite(twcAmount)) {
		return DEFAULT_RESULT;
	}

	// Absolute (Starter): raw TWC price, no delta.
	if (isAbsolute) {
		return formatResult(currency, twcAmount, 'absolute');
	}

	// Delta case needs a Loom baseline; without one, fall back.
	if (typeof loomBaseline !== 'number') {
		return DEFAULT_RESULT;
	}

	const delta = twcAmount - loomBaseline;
	if (!Number.isFinite(delta) || delta === 0) {
		return DEFAULT_RESULT;
	}

	// Positive → TWC costs more; negative → cheaper.
	return formatResult(currency, Math.abs(delta), delta > 0 ? 'more' : 'less');
};

/**
 * Live, localized choice-card price for a given Loom edition. The edition
 * selects the compared TWC edition and delta-vs-absolute mode.
 */
export const useTwcPrice = (edition: LoomEdition = DEFAULT_LOOM_EDITION): UseTwcPriceResult => {
	const deliveryKey = process.env['PRODUCT_CATALOG_DELIVERY_KEY'] as string;
	const space = process.env['PRODUCT_CATALOG_SPACE'] as string;
	const locationBaseUrl = process.env['PRODUCT_CATALOG_LOCATION_BASE_URL'] as string;

	const twcSku = LOOM_EDITION_TO_TWC_SKU[edition];
	const loomSku = LOOM_EDITION_TO_SKU[edition];
	const isAbsolute = ABSOLUTE_PRICE_EDITIONS.has(edition);

	const {
		prices: ccpPricingData,
		status: ccpStatus,
		fetchData: fetchCcpData,
	} = useCCPPriceFinderData([twcSku, loomSku], deliveryKey, space);

	// The CCP hook has no error state and never resolves its status if the fetch
	// rejects, so track failures ourselves. `hasError` also covers the timeout.
	const [hasError, setHasError] = useState(false);

	useEffect(() => {
		// Wrap in Promise.resolve so a non-promise return can't throw here, and
		// surface any rejection as an error (the CCP hook has no error state).
		Promise.resolve(fetchCcpData()).catch(() => setHasError(true));
	}, [fetchCcpData]);

	const { countryCode, status: locationStatus } = useGeoLocation(locationBaseUrl);

	const currency = getCurrencyFromUserLocale(countryCode ?? '');

	const isLoading = locationStatus !== 'complete' || ccpStatus !== 'complete';

	// Guard against a hung request (fetch that neither resolves nor rejects):
	// give up after the timeout so callers can fall back instead of spinning.
	useEffect(() => {
		if (!isLoading) {
			return;
		}
		const timer = setTimeout(() => setHasError(true), PRICE_FETCH_TIMEOUT_MS);
		return () => clearTimeout(timer);
	}, [isLoading]);

	if (hasError) {
		return ERROR_RESULT;
	}

	const hasData = Boolean(ccpPricingData && countryCode);
	if (isLoading || !hasData) {
		return { price: DEFAULT_TWC_PRICE, mode: DEFAULT_RESULT_MODE, isLoading, hasError: false };
	}

	const twcAmount = readMonthlyPerUserAmount(ccpPricingData?.[twcSku], currency);
	// For the delta case we also need the Loom baseline (free → 0). The absolute
	// case ignores it. `resolvePriceResult` handles all the miss/fallback branches.
	const loomBaseline = resolveLoomBaseline(ccpPricingData?.[loomSku], currency);

	return resolvePriceResult(twcAmount, loomBaseline, currency, isAbsolute);
};
